import React, { createContext, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGetCurrentUserProfileQuery } from '../../redux/api/authorized/auth';
import { selectPrivyToken } from '../../redux/slices/auth';
import { useSelector } from 'react-redux';

const giftFormSchema = yup
  .object({
    delivery: yup
      .string()
      .oneOf(['email', 'link'], 'Delivery method must be email or link')
      .required('Delivery method is required'),
    isUsingUSDC: yup.boolean(),
    userEmail: yup.string(),
    email: yup
      .string()
      .email('Must be a valid email')
      .when('delivery', {
        is: 'email',
        then: (schema) =>
          schema
            .required('Email is required')
            .test(
              'email-match',
              'You cannot use your own email',
              (value, context) => value !== context.parent.userEmail,
            ),
        otherwise: (schema) => schema.optional(),
      }),
    message: yup
      .string()
      .optional()
      .max(190, 'Message must be at most 190 characters'),
    image: yup.string().required('Card image is required'),
    network: yup.string().required('Network is required'),
    targetTokenAddress: yup.string().required('Token address is required'),
    usdAmount: yup.string().when('isUsingUSDC', {
      is: true,
      then: (schema) =>
        schema
          .required('Amount is required')
          .test('is-number', 'Must be a valid number', (value) =>
            value === '' || value == null ? false : !isNaN(Number(value)),
          )
          .test('min-1', 'Minimum amount is 1', (value) => {
            const n = Number(value);
            return !isNaN(n) && n >= 1;
          }),
      otherwise: (schema) => schema.optional(),
    }),
    targetTokenAmount: yup.string().when('isUsingUSDC', {
      is: false,
      then: (schema) =>
        schema
          .required('Amount is required')
          .test('is-number', 'Must be a valid number', (value) =>
            value === '' || value == null ? false : !isNaN(Number(value)),
          )
          .test('min-1', 'Must be greater than 0', (value) => {
            const n = Number(value);
            return !isNaN(n) && n > 0;
          }),
      otherwise: (schema) => schema.optional(),
    }),
  })
  .required();

const GiftFormContext = createContext(null);

const defaultInitialValues = {
  isUsingUSDC: true,
  delivery: 'link',
  usdAmount: '25',
  targetTokenAmount: '',
};

export const GiftFormProvider = ({
  children,
  schema = giftFormSchema,
  mode = 'all',
}) => {
  const form = useForm({
    resolver: yupResolver(schema),
    mode,
    defaultValues: {
      ...defaultInitialValues,
    },
  });

  const privyToken = useSelector(selectPrivyToken);
  const { data: currentUser } = useGetCurrentUserProfileQuery(undefined, {
    skip: !privyToken,
  });

  const userEmail = currentUser?.email;

  const {
    watch,
    setValue,
    clearErrors,
    trigger,
    formState: { errors, isValid, isSubmitting },
    handleSubmit,
    control,
  } = form;

  const [selectedPaymentToken, setSelectedPaymentToken] = useState(null);
  const [sendingStatus, setSendingStatus] = useState('idle');
  const [feedback, setFeedback] = useState(null);
  const [claimUrl, setClaimUrl] = useState(null);
  const [fromUsdcDirection, setFromUsdcDirection] = useState(true);
  const [gift, setGift] = useState(null);
  const [payingWith, setPayingWith] = useState(null);
  const isUsingUSDC = watch('isUsingUSDC');
  const delivery = watch('delivery');
  const email = watch('email');
  const message = watch('message');
  const image = watch('image');
  const targetTokenAddress = watch('targetTokenAddress');
  const network = watch('network');
  const usdAmount = watch('usdAmount');
  const targetTokenAmount = watch('targetTokenAmount');

  useEffect(() => {
    if (userEmail && setValue) {
      setValue('userEmail', userEmail);
    }
  }, [userEmail, setValue]);

  useEffect(() => {
    trigger('email');
  }, [delivery]);

  const initializeForm = ({ initialValues }) => {
    setSelectedPaymentToken(null);
    setClaimUrl(null);
    setSendingStatus('idle');
    setFeedback(null);
    setFromUsdcDirection(true);
    form.reset({
      ...defaultInitialValues,
      ...initialValues,
      userEmail,
    });
    clearErrors();
  };

  return (
    <GiftFormContext.Provider
      value={{
        handleSubmit,
        control,
        errors,
        isValid,
        isSubmitting,
        setValue,
        claimUrl,
        gift,
        setGift,
        setFeedback,
        setClaimUrl,
        setSendingStatus,
        clearErrors,
        trigger,
        payingWith,
        setPayingWith,
        isUsingUSDC,
        delivery,
        email,
        message,
        image,
        network,
        sendingStatus,
        feedback,
        usdAmount,
        selectedPaymentToken,
        setSelectedPaymentToken,
        targetTokenAddress,
        targetTokenAmount,
        initializeForm,
        setIsUsingUSDC: (newValue) => setValue('isUsingUSDC', newValue),
        fromUsdcDirection,
        setFromUsdcDirection,
      }}
    >
      {children}
    </GiftFormContext.Provider>
  );
};

export const useGiftForm = () => {
  const ctx = useContext(GiftFormContext);
  if (!ctx)
    throw new Error('useGiftForm must be used within a GiftFormProvider');
  return ctx;
};

export { giftFormSchema };
